import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom';
import { MdCompare } from 'react-icons/md';
import { getMatchMeta } from '../../../Screens/User/Quotations';

const ComparisonModal = ({ quotations, onClose, onSelectQuotation, cartQuotationIds, sampleCart, selectedQuotations }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => { setTimeout(() => setAnimate(true), 10); }, []);

  const handleClose = () => { setAnimate(false); setTimeout(onClose, 250); };

  const getNestedVal = (obj, path) => {
    if (!path) return undefined;
    return path.split(".").reduce((acc, k) => acc && acc[k], obj);
  };

  const rows = [
    {
      label: "Price Per kg",
      icon: "🏷️",
      scorePath: "matchingBreakdown.price.score",
      getValue: (q) => `${q.currency || "₹"} ${(q.pricing?.commissionAdjustedAmount || q.pricing?.amount || 0).toLocaleString("en-IN")}`,
      priceRow: true,
    },
    {
      label: "Min Qty (MOQ)",
      icon: "📦",
      scorePath: "matchingBreakdown.quantity.score",
      getValue: (q) => `${q.quantity?.volume || 0} ${q.standardizedUnit || "kg"}`,
    },
    {
      label: "Availability",
      icon: "✅",
      scorePath: "matchingBreakdown.quantity.score",
      getValue: (q) => q.quantity?.isAvailableFull ? "Full Order" : "Partial",
    },
    {
      label: "Answered Qs",
      icon: "❓",
      scorePath: "matchingBreakdown.relatedQuestions.score",
      getValue: (q) => q.matchingBreakdown?.relatedQuestions?.detail || "N/A",
    },
    {
      label: "Holding Period",
      icon: "🕐",
      scorePath: "matchingBreakdown.deliveryWindow.score",
      getValue: (q) => {
        if (q.holdingPeriod?.from && q.holdingPeriod?.to) {
          const diff = Math.abs(new Date(q.holdingPeriod.to) - new Date(q.holdingPeriod.from));
          return `${Math.ceil(diff / 86400000)} Days`;
        }
        return "N/A";
      },
    },
    {
      label: "Agreed Terms",
      icon: "📄",
      scorePath: "matchingBreakdown.otherRequirements.score",
      getValue: (q) => q.matchingBreakdown?.otherRequirements?.detail || "N/A",
    },
    {
      label: "Match %",
      icon: "⚡",
      scorePath: "matchingPercentage",
      getValue: (q) => `${q.matchingPercentage || 0}%`,
      matchRow: true,
    },
  ];

  const bestQuotation = useMemo(() =>
    [...quotations].sort((a, b) => (b.matchingPercentage || 0) - (a.matchingPercentage || 0))[0],
    [quotations]
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"
          }`}
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center flex-shrink-0">
          <button onClick={handleClose} className="text-2xl mr-3">←</button>
          <div className="flex-1">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <MdCompare /> Compare Quotations
            </h2>
            <p className="text-xs text-white/70 mt-0.5">{quotations.length} quotations side by side</p>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-auto flex-1">
          <div className="m-4 rounded-2xl border border-gray-200 overflow-hidden shadow-sm">

            {/* Top header cards */}
            <div className="flex border-b border-gray-200 bg-white">
              {quotations.map((q, idx) => {
                const pct = q.matchingPercentage;
                const meta = pct !== undefined ? getMatchMeta(pct) : null;
                const isBest = q._id === bestQuotation?._id;
                return (
                  <div
                    key={q._id}
                    className={`flex-1 p-3 flex flex-col items-center gap-1.5 ${idx > 0 ? "border-l border-gray-200" : ""} ${isBest ? "bg-green-50" : ""}`}
                  >
                    {isBest && (
                      <span className="text-[9px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        ⭐ BEST
                      </span>
                    )}
                    {/* <div className="w-9 h-9 rounded-full bg-primaryColor/10 flex items-center justify-center text-primaryColor font-extrabold text-sm">
                      {q.createdBy?.firstName?.charAt(0) || "S"}
                    </div>
                    <p className="text-xs font-extrabold text-gray-900 text-center leading-tight">
                      {q.productVariety?.name || q.createdBy?.firstName || `Quote ${idx + 1}`}
                    </p> */}
                    {/* Batch tag */}
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-2 py-0.5">
                      <span className="text-[9px]">📦</span>
                      <span className="text-gray-600 text-[9px] font-bold">
                        {q.quotationUniqueNumber}
                      </span>
                    </div>
                    {/* Match % pill */}
                    {meta && (
                      <div className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${meta.bg} ${meta.border} ${meta.text}`}>
                        {pct}% · {meta.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Data grid rows */}
            {rows.map((row, rowIdx) => {
              const isLast = rowIdx === rows.length - 1;
              const scores = quotations.map((q) => {
                const v = getNestedVal(q, row.scorePath);
                return typeof v === "number" ? v : 0;
              });
              const maxScore = Math.max(...scores);

              return (
                <div key={rowIdx} className={`flex ${!isLast ? "border-b border-gray-100" : ""}`}>
                  {quotations.map((q, colIdx) => {
                    const cellScore = getNestedVal(q, row.scorePath);
                    const isWinner = typeof cellScore === "number" && cellScore > 0 && cellScore === maxScore;

                    return (
                      <div
                        key={q._id}
                        className={`flex-1 flex flex-col items-center justify-center py-3.5 px-2 gap-1 ${colIdx > 0 ? "border-l border-gray-100" : ""
                          } ${isWinner ? "bg-green-50" : "bg-white"}`}
                      >
                        {/* Label + icon (shown in every cell so each column is self-contained) */}
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-[11px]">{row.icon}</span>
                          <span className={`text-[10px] font-semibold ${isWinner ? "text-green-700" : "text-gray-500"}`}>
                            {row.label}
                          </span>
                        </div>

                        {/* Value */}
                        <span className={`text-sm font-extrabold text-center leading-tight ${row.priceRow
                          ? isWinner ? "text-green-700" : "text-red-700"
                          : row.matchRow
                            ? "text-green-600"
                            : isWinner
                              ? "text-green-700"
                              : "text-gray-900"
                          }`}>
                          {row.getValue(q)}
                        </span>

                        {/* Winner badge */}
                        {isWinner && (
                          <span className="text-[9px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">
                            ✓ Best
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Best recommendation banner */}
          {bestQuotation && (() => {
            const meta = getMatchMeta(bestQuotation.matchingPercentage || 0);
            return (
              <div className={`mx-4 mb-4 rounded-2xl border-2 p-4 ${meta.bg} ${meta.border}`}>
                <p className={`text-xs font-bold mb-1 ${meta.text}`}>⭐ OUR RECOMMENDATION</p>
                <p className="text-sm font-extrabold text-gray-900">
                  {bestQuotation.quotationUniqueNumber || bestQuotation.productVariety?.name || bestQuotation.createdBy?.firstName || "Top Quote"} – {bestQuotation.matchingPercentage}% match
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Best overall score across all compared quotations</p>
              </div>
            );
          })()}
        </div>

        {/* Footer: select per quotation */}
        <div className="p-3 md:p-5 border-t flex gap-3 flex-shrink-0 bg-gray-50">
          <button
            onClick={handleClose}
            className="flex-shrink-0 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors text-sm"
          >
            Cancel
          </button>
          {quotations.map((q, i) => {
            const isSampleSelected = q.buyerDecision === "Proceed_With_Sample";
            const isFullOrderSelected =
              q.isSelectedByBuyer &&
              q.buyerDecision === "Proceed_Full_Order" &&
              !q.sampleDetails?.requested;
            const isInCart = cartQuotationIds?.has(q._id?.toString());
            const sampleModeActive =
              (sampleCart?.items?.length > 0) || (selectedQuotations?.length > 0);

            // Determine button label + style
            let label, btnClass, disabled;

            if (isSampleSelected || isFullOrderSelected) {
              label = isSampleSelected ? "✓ Sample Selected" : "✓ Full Order";
              btnClass =
                "flex-1 py-2 md:py-3 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-xl font-semibold text-xs md:text-sm cursor-default";
              disabled = true;
            } else if (isInCart) {
              label = "✓ In Cart";
              btnClass =
                "flex-1 py-2 md:py-3 bg-primaryColor/10 border border-primaryColor/30 text-primaryColor rounded-xl font-semibold text-xs md:text-sm cursor-default";
              disabled = true;
            } else if (sampleModeActive) {
              label = "＋ Add to Cart";
              btnClass =
                "flex-1 py-2 md:py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-xl font-semibold transition-colors text-xs md:text-sm";
              disabled = false;
            } else {
              label = `Select ${q.quotationUniqueNumber || `Quote ${i + 1}`}`;
              btnClass =
                "flex-1 py-2 md:py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-xl font-semibold transition-colors text-xs md:text-sm";
              disabled = false;
            }

            return (
              <button
                key={q._id}
                onClick={() => !disabled && onSelectQuotation(q)}
                disabled={disabled}
                className={btnClass}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ComparisonModal